// ---------------------------------------------------------------------------
// bound-block-structural-gate.ts — VERBATIM PORT of the platform gate
// (cogo-platform apps/cogo-cloudflare/src/lib/agentic/bound-block-structural-gate.ts).
//
// Review D-7: the gate was platform-native single-sided — studio Channel-1
// shipped bound blocks with NO gate even in audit mode. This port closes the
// cogo-web leg (B1-B3) in AUDIT mode (observe + warn, never reject).
// RB-B-5 parity note: keep this file byte-aligned with the platform source
// (same review flagged hand-duplicated retrieve+bind logic drifting); when
// editing, change the platform copy first and re-port.
// ---------------------------------------------------------------------------

export type BoundBlockIssueSeverity = "error" | "warning";

export interface BoundBlockStructuralIssue {
  severity: BoundBlockIssueSeverity;
  code: string;
  path: string;
  message: string;
}

export interface BoundBlockStructuralMetrics {
  nodeCount: number;
  expandedRefCount: number;
  unresolvedRefCount: number;
  unresolvedSlotCount: number;
}

export interface BoundBlockStructuralResult {
  ok: boolean;
  value: Record<string, unknown> | null;
  errors: string[];
  warnings: string[];
  issues: BoundBlockStructuralIssue[];
  metrics: BoundBlockStructuralMetrics;
}

export interface VerifyBoundBlockStructuralOptions {
  maxNodes?: number;
  unresolvedRefSeverity?: BoundBlockIssueSeverity;
}

interface GateState {
  issues: BoundBlockStructuralIssue[];
  expandedRefCount: number;
  unresolvedRefCount: number;
  unresolvedSlotCount: number;
}

const DEFAULT_MAX_NODES = 1_000;
const BIND_SLOT_PATTERN = /#\{__(?:bind|i18n|app)\.[^}]+\}|@ds:[A-Za-z0-9_.:-]+/g;
const DEFINITION_KEYS = new Set(["$defs", "definitions"]);
const SYNTHETIC_BUNDLE_KEYS = ["block", "ui", "events", "flow", "variables", "datasource"];

export function verifyBoundBlockStructural(
  input: unknown,
  options: VerifyBoundBlockStructuralOptions = {},
): BoundBlockStructuralResult {
  const state: GateState = {
    issues: [],
    expandedRefCount: 0,
    unresolvedRefCount: 0,
    unresolvedSlotCount: 0,
  };
  const unresolvedRefSeverity = options.unresolvedRefSeverity ?? "warning";
  const expanded = expandRefs(input, input, "$", [], state, unresolvedRefSeverity);

  if (!isRecord(expanded)) {
    addIssue(state, "error", "root:not-object", "$", "Bound block root must be an object.");
  }

  if (isSyntheticBundle(expanded)) {
    addIssue(
      state,
      "error",
      "root:synthetic-bundle",
      "$",
      "Retrieve-bind must return a UI root subtree, not a synthetic six-key BlockBundle.",
    );
  }

  scanUnresolvedSlots(expanded, "$", state);
  const nodeCount = lintAndCountNodes(expanded, "$", state);
  const maxNodes = options.maxNodes ?? DEFAULT_MAX_NODES;
  if (nodeCount === 0) {
    addIssue(
      state,
      "error",
      "nodes:empty",
      "$",
      "Bound block must contain at least one UI node with a non-empty type.",
    );
  }
  if (nodeCount > maxNodes) {
    addIssue(
      state,
      "error",
      "nodes:too-many",
      "$",
      `Bound block has ${nodeCount} typed nodes; max allowed is ${maxNodes}.`,
    );
  }

  const errors = issueLines(state.issues.filter((issue) => issue.severity === "error"));
  const warnings = issueLines(state.issues.filter((issue) => issue.severity === "warning"));

  return {
    ok: errors.length === 0,
    value: isRecord(expanded) ? expanded : null,
    errors,
    warnings,
    issues: state.issues,
    metrics: {
      nodeCount,
      expandedRefCount: state.expandedRefCount,
      unresolvedRefCount: state.unresolvedRefCount,
      unresolvedSlotCount: state.unresolvedSlotCount,
    },
  };
}

function expandRefs(
  node: unknown,
  root: unknown,
  path: string,
  refStack: string[],
  state: GateState,
  unresolvedRefSeverity: BoundBlockIssueSeverity,
): unknown {
  if (Array.isArray(node)) {
    return node.map((item, index) =>
      expandRefs(item, root, `${path}[${index}]`, refStack, state, unresolvedRefSeverity),
    );
  }
  if (!isRecord(node)) return node;

  const refValue = node.$ref;
  if (typeof refValue === "string") {
    if (!refValue.startsWith("#")) {
      state.unresolvedRefCount++;
      addIssue(
        state,
        unresolvedRefSeverity,
        "ref:external",
        `${path}.$ref`,
        `External $ref is not expanded by the Worker structural gate: ${refValue}`,
      );
    } else if (refStack.includes(refValue)) {
      state.unresolvedRefCount++;
      addIssue(
        state,
        "error",
        "ref:cycle",
        `${path}.$ref`,
        `Cyclic local $ref detected: ${[...refStack, refValue].join(" -> ")}`,
      );
    } else {
      const resolved = resolveJsonPointer(root, refValue);
      if (resolved.found) {
        state.expandedRefCount++;
        const expanded = expandRefs(
          resolved.value,
          root,
          path,
          [...refStack, refValue],
          state,
          unresolvedRefSeverity,
        );
        const siblings = Object.entries(node).filter(([key]) => key !== "$ref");
        if (siblings.length === 0) return expanded;
        if (isRecord(expanded)) {
          const merged: Record<string, unknown> = { ...expanded };
          for (const [key, value] of siblings) {
            merged[key] = expandRefs(
              value,
              root,
              appendPath(path, key),
              refStack,
              state,
              unresolvedRefSeverity,
            );
          }
          return merged;
        }
        addIssue(
          state,
          "error",
          "ref:sibling-non-object",
          path,
          "A $ref with sibling fields must resolve to an object.",
        );
      } else {
        state.unresolvedRefCount++;
        addIssue(
          state,
          unresolvedRefSeverity,
          "ref:unresolved",
          `${path}.$ref`,
          `Local $ref target was not found: ${refValue}`,
        );
      }
    }
  }

  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(node)) {
    out[key] = expandRefs(
      value,
      root,
      appendPath(path, key),
      refStack,
      state,
      unresolvedRefSeverity,
    );
  }
  return out;
}

function scanUnresolvedSlots(node: unknown, path: string, state: GateState): void {
  if (typeof node === "string") {
    BIND_SLOT_PATTERN.lastIndex = 0;
    const matches = [...node.matchAll(BIND_SLOT_PATTERN)];
    for (const match of matches) {
      state.unresolvedSlotCount++;
      addIssue(
        state,
        "error",
        "slot:unresolved",
        path,
        `Unresolved retrieve-bind slot remains after binding: ${match[0]}`,
      );
    }
    return;
  }
  if (Array.isArray(node)) {
    node.forEach((item, index) => scanUnresolvedSlots(item, `${path}[${index}]`, state));
    return;
  }
  if (!isRecord(node)) return;
  for (const [key, value] of Object.entries(node)) {
    if (DEFINITION_KEYS.has(key)) continue;
    scanUnresolvedSlots(value, appendPath(path, key), state);
  }
}

function lintAndCountNodes(node: unknown, path: string, state: GateState): number {
  if (Array.isArray(node)) {
    return node.reduce((sum, item, index) => sum + lintAndCountNodes(item, `${path}[${index}]`, state), 0);
  }
  if (!isRecord(node)) return 0;

  let count = 0;
  const typeValue = node.type;
  const isTypedNode = typeof typeValue === "string";
  if (isTypedNode) {
    if (typeValue.trim().length === 0) {
      addIssue(state, "error", "node:type-empty", `${path}.type`, "UI node type must not be empty.");
    } else {
      count++;
    }
    if ("children" in node) {
      if (!Array.isArray(node.children)) {
        addIssue(state, "error", "node:children-not-array", `${path}.children`, "UI node children must be an array.");
      } else {
        node.children.forEach((child, index) => {
          if (!isRecord(child)) {
            addIssue(
              state,
              "error",
              "node:child-not-object",
              `${path}.children[${index}]`,
              "UI node children entries must be objects.",
            );
          }
        });
      }
    }
    if ("layout" in node && !isRecord(node.layout) && typeof node.layout !== "string") {
      addIssue(
        state,
        "warning",
        "layout:invalid-shape",
        `${path}.layout`,
        "UI node layout should be an object or string token.",
      );
    }
    if ("className" in node && typeof node.className !== "string") {
      addIssue(
        state,
        "error",
        "node:className-not-string",
        `${path}.className`,
        "UI node className must be a string when present.",
      );
    }
  }

  for (const [key, value] of Object.entries(node)) {
    if (DEFINITION_KEYS.has(key)) continue;
    count += lintAndCountNodes(value, appendPath(path, key), state);
  }
  return count;
}

function isSyntheticBundle(node: unknown): boolean {
  return isRecord(node) && SYNTHETIC_BUNDLE_KEYS.every((key) => key in node);
}

function resolveJsonPointer(root: unknown, pointer: string): { found: boolean; value?: unknown } {
  if (pointer === "#") return { found: true, value: root };
  if (!pointer.startsWith("#/")) return { found: false };
  let current = root;
  for (const rawPart of pointer.slice(2).split("/")) {
    const part = rawPart.replace(/~1/g, "/").replace(/~0/g, "~");
    if (Array.isArray(current)) {
      const index = Number(part);
      if (!Number.isInteger(index) || index < 0 || index >= current.length) return { found: false };
      current = current[index];
    } else if (isRecord(current) && Object.prototype.hasOwnProperty.call(current, part)) {
      current = current[part];
    } else {
      return { found: false };
    }
  }
  return { found: true, value: current };
}

function addIssue(
  state: GateState,
  severity: BoundBlockIssueSeverity,
  code: string,
  path: string,
  message: string,
): void {
  state.issues.push({ severity, code, path, message });
}

function issueLines(issues: BoundBlockStructuralIssue[]): string[] {
  return issues.map((issue) => `${issue.code} at ${issue.path}: ${issue.message}`);
}

function appendPath(path: string, key: string): string {
  if (/^[A-Za-z_$][A-Za-z0-9_$]*$/.test(key)) return `${path}.${key}`;
  return `${path}[${JSON.stringify(key)}]`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}
